// MetaspaceToolbarMobileOrganism.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { RootState } from '@center-pwa/store/store';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { SphereChatPanel } from 'design-system/my-lib';
import { useMetaspaceEvents } from '../../../hooks/useMetaspaceEvents';
import { useToolbarState } from '../../../context/ToolbarStateContext';
import './MetaspaceToolbarMobileOrganism.scss';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

export type ToolbarItem = 'chatbox' | 'comments' | 'activity' | 'bookmarks';

interface ToolbarItemConfig {
  id: ToolbarItem;
  icon: string;
  label: string;
  count: number;
  color?: string;
}

interface MetaspaceToolbarMobileProps {
  sphereId?: string;
  isOpen?: boolean;
  activeItem?: ToolbarItem;
  onOpenChange?: (isOpen: boolean) => void;
  chatSphereId?: string;
  chatboxContextMode?: 'sphere_only' | 'sphere_and_metaspace';
}

const MetaspaceToolbarMobileOrganism: React.FC<MetaspaceToolbarMobileProps> = ({
  sphereId: sphereIdProp,
  isOpen: controlledIsOpen,
  activeItem: controlledActiveItem,
  onOpenChange,
  chatSphereId,
  chatboxContextMode,
}) => {
  const { t } = useTranslation();

  // --- sphere & metaspace context ---
  const { sphereId: sphereIdFromUrl, metaspaceId } = useParams<{ sphereId?: string; metaspaceId?: string }>();
  const sphereIdFromStore = useSelector((s: RootState) => s.nodes?.currentSphereId || s.nodes?.selectedSphereId);
  const metaspaces = useSelector(selectAllMetaspaces);
  const sphereId = sphereIdProp ?? sphereIdFromUrl ?? sphereIdFromStore ?? 'default';

  // --- UI state ---
  const [isDrawerOpen, setIsDrawerOpen] = useState(controlledIsOpen ?? false);
  const [internalActiveItem, setInternalActiveItem] = useState<ToolbarItem>(controlledActiveItem ?? 'chatbox');
  const [activeChatSphereId, setActiveChatSphereId] = useState<string | undefined>(chatSphereId);
  const [activeMetaspaceId, setActiveMetaspaceId] = useState<string | undefined>(metaspaceId);

  const { setIsExpanded } = useToolbarState();
  const activeItem = controlledActiveItem ?? internalActiveItem;

  // Toolbar items configuration - Only Chat
  const toolbarItems: ToolbarItemConfig[] = [
    { id: 'chatbox', icon: 'pi pi-comment', label: t('toolbar.chat'), count: 0, color: 'danger' },
  ];

  // Event handlers
  const handleChatTriggeredEvent = useCallback(
    (payload: any) => {
      if (payload.sphereId) {
        setActiveChatSphereId(payload.sphereId);
      }
      if (payload.metaspaceId) {
        setActiveMetaspaceId(payload.metaspaceId);
      }
      if (controlledIsOpen === undefined) {
        setIsDrawerOpen(true);
      }
      if (controlledActiveItem === undefined) {
        setInternalActiveItem('chatbox');
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleToolbarExpandEvent = useCallback(
    (payload: any) => {
      if (payload.activeItem && controlledActiveItem === undefined) {
        setInternalActiveItem(payload.activeItem as ToolbarItem);
      }
      if (controlledIsOpen === undefined) {
        setIsDrawerOpen(true);
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleThreadContextLoadEvent = useCallback((payload: any) => {
    if (payload.sphereId) {
      setActiveChatSphereId(payload.sphereId);
    }
    if (payload.metaspaceId) {
      setActiveMetaspaceId(payload.metaspaceId);
    }
  }, []);

  const handleMetaspaceEvent = useCallback(
    (event: { type: string; payload: any }) => {
      switch (event.type) {
        case 'CHAT_TRIGGERED':
          handleChatTriggeredEvent(event.payload);
          break;
        case 'TOOLBAR_EXPAND':
          handleToolbarExpandEvent(event.payload);
          break;
        case 'THREAD_CONTEXT_LOAD':
          handleThreadContextLoadEvent(event.payload);
          break;
        default:
          break;
      }
    },
    [handleChatTriggeredEvent, handleToolbarExpandEvent, handleThreadContextLoadEvent]
  );

  const eventTypes = useMemo(() => ['CHAT_TRIGGERED', 'TOOLBAR_EXPAND', 'THREAD_CONTEXT_LOAD'], []);

  useMetaspaceEvents({
    subscriberId: 'metaspace-toolbar-mobile',
    eventTypes,
    onEvent: handleMetaspaceEvent,
  });

  // Context key for chat
  const contextKey = useMemo(() => {
    const isChatboxActive = activeItem === 'chatbox';
    const hasChatSphereId = activeChatSphereId || chatSphereId;
    const currentSphereId = isChatboxActive && hasChatSphereId ? activeChatSphereId || chatSphereId : sphereId;

    // If chatbox contextMode is sphere_only, skip metaspace segment entirely
    if (chatboxContextMode === 'sphere_only') {
      return `sphere:${currentSphereId}`;
    }

    const currentMetaspaceId = activeMetaspaceId || metaspaceId;

    if (currentMetaspaceId) {
      // Resolve system_name to UUID if needed
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      let resolvedMetaspaceId = currentMetaspaceId;

      if (!uuidRegex.test(currentMetaspaceId) && metaspaces?.length > 0) {
        const found = metaspaces.find((m: any) => m.system_name === currentMetaspaceId);
        if (found?.metaspace_id) {
          resolvedMetaspaceId = found.metaspace_id;
        }
      }

      return `sphere:${currentSphereId}|metaspace:${resolvedMetaspaceId}`;
    }
    return `sphere:${currentSphereId}`;
  }, [activeItem, activeChatSphereId, chatSphereId, sphereId, activeMetaspaceId, metaspaceId, chatboxContextMode, metaspaces]);

  // Sync with controlled props
  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsDrawerOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  useEffect(() => {
    if (controlledActiveItem !== undefined) {
      setInternalActiveItem(controlledActiveItem);
    }
  }, [controlledActiveItem]);

  useEffect(() => {
    if (metaspaceId !== undefined) {
      setActiveMetaspaceId(metaspaceId);
    }
  }, [metaspaceId]);

  useEffect(() => {
    setIsExpanded(isDrawerOpen);
  }, [isDrawerOpen, setIsExpanded]);

  // Handlers
  const handleToggleDrawer = () => {
    const newIsOpen = !isDrawerOpen;
    if (controlledIsOpen === undefined) {
      setIsDrawerOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };

  const handleItemClick = (itemId: ToolbarItem) => {
    if (controlledActiveItem === undefined) {
      setInternalActiveItem(itemId);
    }
    // Keep drawer open when switching items
    if (!isDrawerOpen && controlledIsOpen === undefined) {
      setIsDrawerOpen(true);
      onOpenChange?.(true);
    }
  };

  const handleBackdropKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDrawer();
    }
    if (event.key === 'Escape') {
      if (isDrawerOpen && controlledIsOpen === undefined) {
        setIsDrawerOpen(false);
        onOpenChange?.(false);
      }
    }
  };

  const getContent = () => {
    // Only chat is available
    return (
      <div className="drawer-content-panel">
        <SphereChatPanel contextKey={contextKey} collapsible={false} scrollToBottomOnLoad={true} showThreadContext={true} />
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {isDrawerOpen && (
        <button
          type="button"
          className="mobile-toolbar-backdrop"
          onClick={handleToggleDrawer}
          onKeyDown={handleBackdropKeyDown}
          aria-label={t('toolbar.close')}
        />
      )}

      {/* Mobile Toolbar Container - positioned from bottom nav bar */}
      <div className={`mobile-toolbar-container ${isDrawerOpen ? 'expanded' : ''}`}>
        {/* Drawer Panel */}
        <div className="mobile-toolbar-drawer">
          {/* Drawer Header */}
          <div className="drawer-header">
            <div className="drawer-handle" />
            <div className="drawer-tabs">
              {toolbarItems.map((item) => (
                <button
                  key={item.id}
                  className={`drawer-tab ${activeItem === item.id ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.id)}
                >
                  <div className="tab-icon-wrapper">
                    <i className={item.icon} />
                    {item.count > 0 && <span className="tab-badge">{item.count}</span>}
                  </div>
                  <span className="tab-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Drawer Content */}
          <div className="drawer-content">{getContent()}</div>
        </div>
      </div>
    </>
  );
};

export default MetaspaceToolbarMobileOrganism;
